import { EmailEntityId, SentMessageId } from "@sp-crm/core";
import React, { useMemo, useState } from "react";
import { useFeature } from "store/selectors/hooks";
import {
    useGetConversationFeedForEntityQuery,
    useGetUserMailboxMessagesForEntityQuery,
} from "../../generated/graphql";
import { ErrorMessage } from "../ui/error-message";
import { Spinner } from "../ui/spinner";
import { MessageRow } from "./message-row";
import { MessageRowExpanded } from "./message-row-expanded";
import { SentMessageDetail } from "./sent-detail";
import { mergeAndSortMessages, UnifiedMessage } from "./unified-message";

interface UnifiedMessageFeedProps {
    entityId: EmailEntityId;
    showClient?: boolean;
    showCommunity?: boolean;
}

export const UnifiedMessageFeed: React.FC<UnifiedMessageFeedProps> = props => {
    const { entityId } = props;

    const mailboxSyncEnabled = useFeature("mailboxSync");

    const conversationQuery = useGetConversationFeedForEntityQuery({ entityId });
    const mailboxQuery = useGetUserMailboxMessagesForEntityQuery(
        { id: entityId },
        { enabled: mailboxSyncEnabled },
    );

    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [viewingDetailId, setViewingDetailId] = useState<SentMessageId | null>(null);

    const isLoading =
        conversationQuery.isLoading || (mailboxSyncEnabled && mailboxQuery.isLoading);
    const hasError = conversationQuery.isError || mailboxQuery.isError;

    const messages: UnifiedMessage[] = useMemo(() => {
        const receivedMessages = mailboxQuery.data?.getUserMailboxMessagesForEntity ?? [];
        const conversationFeed = conversationQuery.data?.getConversationFeedForEntity;
        return mergeAndSortMessages(receivedMessages, conversationFeed);
    }, [mailboxQuery.data, conversationQuery.data]);

    const handleToggleExpand = (id: string) => {
        setExpandedId(current => (current === id ? null : id));
    };

    const handleViewFullSentMessage = (id: SentMessageId) => {
        setViewingDetailId(id);
    };

    const handleCloseDetail = () => {
        setViewingDetailId(null);
    };

    if (isLoading) {
        return <Spinner />;
    }

    if (hasError) {
        return (
            <ErrorMessage component="UnifiedMessageFeed">
                <pre>
                    {JSON.stringify(
                        conversationQuery.error || mailboxQuery.error,
                        null,
                        2,
                    )}
                </pre>
            </ErrorMessage>
        );
    }

    return (
        <div className="flex flex-col max-w-screen-lg">
            {viewingDetailId ? (
                <SentMessageDetail
                    messageId={viewingDetailId}
                    onDismiss={handleCloseDetail}
                />
            ) : null}

            <div className="flow-root">
                <ul role="list">
                    {messages.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                            <em>No messages yet</em>
                        </div>
                    ) : (
                        messages.map(message => (
                            <MessageRow
                                key={message.id}
                                message={message}
                                isExpanded={expandedId === message.id}
                                onToggleExpand={() => handleToggleExpand(message.id)}>
                                <MessageRowExpanded
                                    message={message}
                                    onViewFullSentMessage={handleViewFullSentMessage}
                                />
                            </MessageRow>
                        ))
                    )}
                </ul>
            </div>
        </div>
    );
};
